import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LetterheadSettings } from '@his/shared';
import { del, post, put, uploadWithProgress } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tenantSecondaryLanguages } from '@/lib/locale-fit';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input, SkeletonText, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';

type Line = LetterheadSettings['left'][number];

const BLANK: LetterheadSettings = {
  layout: 'single',
  left: [],
  right: [],
  showBranchAddress: true,
  showBranchPhone: true,
  showBranchLicence: true,
  footer: '',
};

function padLines(lines: Line[], dir: 'ltr' | 'rtl'): Line[] {
  const out = [...lines];
  while (out.length < 4) out.push({ text: '', dir });
  return out.slice(0, 4);
}

function cleanLines(lines: Line[]): Line[] {
  return lines.filter((line) => line.text.trim().length > 0);
}

export function uploadFile<T = unknown>(path: string, file: File): Promise<T> {
  const body = new FormData();
  body.append('file', file);
  return uploadWithProgress<T>(path, body, () => undefined);
}

/** The printed letterhead and logo, with a live preview of what documents will carry. */
export function LetterheadCard({
  readOnly,
  stored,
  logoUrl,
}: {
  readOnly: boolean;
  stored: LetterheadSettings | undefined;
  logoUrl: string | null;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<LetterheadSettings>(() => ({ ...BLANK, ...(stored ?? {}) }));
  const { user } = useAuth();
  const hasSecondary = tenantSecondaryLanguages(user).length > 0;
  const [html, setHtml] = useState<string>('');

  useEffect(() => {
    setDraft({ ...BLANK, ...(stored ?? {}) });
  }, [stored]);

  const payload = (): LetterheadSettings => ({
    ...draft,
    left: cleanLines(draft.left),
    right: draft.layout === 'bilingual' ? cleanLines(draft.right) : [],
    footer: draft.footer?.trim() || undefined,
  });

  const preview = useQuery({
    queryKey: ['letterhead-preview', JSON.stringify(payload()), logoUrl],
    queryFn: () => post<{ html: string }>('/org/branding/preview', { letterhead: payload() }),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (preview.data) setHtml(preview.data.html);
  }, [preview.data]);

  const save = useMutation({
    mutationFn: () => put('/org/settings', { letterhead: payload() }),
    onSuccess: () => {
      toast.success(tr('admin', 'letterhead_saved'), tr('admin', 'new_documents_print_with_it'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_letterhead'), describeError(error)),
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadFile('/org/branding/logo', file),
    onSuccess: () => {
      toast.success(tr('admin', 'logo_uploaded'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['letterhead-preview'] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_upload_the_logo'), describeError(error)),
  });

  const removeLogo = useMutation({
    mutationFn: () => del('/org/branding/logo'),
    onSuccess: () => {
      toast.success(tr('admin', 'logo_removed'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['letterhead-preview'] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_remove_the_logo'), describeError(error)),
  });

  const uploadedLogo = Boolean(logoUrl?.startsWith('/api/v1/public/branding/'));
  const setLine = (side: 'left' | 'right', index: number, text: string) =>
    setDraft((current) => {
      const lines = padLines(current[side], side === 'right' ? 'rtl' : 'ltr');
      lines[index] = { ...lines[index], text };
      return { ...current, [side]: lines };
    });
  const toggleDir = (side: 'left' | 'right', index: number) =>
    setDraft((current) => {
      const lines = padLines(current[side], side === 'right' ? 'rtl' : 'ltr');
      lines[index] = { ...lines[index], dir: lines[index].dir === 'rtl' ? 'ltr' : 'rtl' };
      return { ...current, [side]: lines };
    });

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-sm">{tr('admin', 'letterhead_and_logo')}</CardTitle>
        {!readOnly && (
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
            {tr('admin', 'save_letterhead')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <Field label={tr('admin', 'logo')} hint={tr('admin', 'png_jpeg_or_webp_up_to_512_kb_printed_on')}>
            <div className="flex flex-wrap items-center gap-2">
              {logoUrl && <img src={logoUrl} alt="" className="h-10 w-10 rounded border object-contain" />}
              {!readOnly && (
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="max-w-xs"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) upload.mutate(file);
                  }}
                />
              )}
              {!readOnly && logoUrl && (
                <Button variant="ghost" size="sm" onClick={() => removeLogo.mutate()} loading={removeLogo.isPending}>
                  {tr('admin', 'remove')}
                </Button>
              )}
            </div>
          </Field>
          {logoUrl && !uploadedLogo && <Alert variant="warning">{tr('admin', 'upload_your_logo_to_print_it_a_link_to_another')}</Alert>}
          <Field label={tr('admin', 'layout')}>
            <ChoiceButtons<LetterheadSettings['layout']>
              value={draft.layout}
              disabled={readOnly}
              onChange={(layout) => setDraft({ ...draft, layout })}
              options={[
                { value: 'single', label: tr('admin', 'one_language') },
                ...(hasSecondary || draft.layout === 'bilingual'
                  ? [{ value: 'bilingual' as const, label: tr('admin', 'two_languages_second_one_on_the_right') }]
                  : []),
              ]}
            />
          </Field>
          <div className={draft.layout === 'bilingual' ? 'grid gap-3 sm:grid-cols-2' : ''}>
            {(['left', ...(draft.layout === 'bilingual' ? ['right'] : [])] as ('left' | 'right')[]).map((side) => (
              <div key={side} className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">{side === 'left' ? tr('admin', 'lines') : tr('admin', 'second_language_lines')}</p>
                {padLines(draft[side], side === 'right' ? 'rtl' : 'ltr').map((line, index) => (
                  <div key={index} className="flex gap-1">
                    <Input
                      dir={line.dir}
                      maxLength={120}
                      placeholder={index === 0 ? tr('admin', 'facility_name_as_printed') : tr('admin', 'address_phone_licence')}
                      value={line.text}
                      disabled={readOnly}
                      onChange={(event) => setLine(side, index, event.target.value)}
                    />
                    <Button type="button" variant="ghost" size="sm" disabled={readOnly} onClick={() => toggleDir(side, index)}>
                      {line.dir === 'rtl' ? tr('admin', 'rtl') : tr('admin', 'ltr')}
                    </Button>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="grid gap-1">
            <Checkbox
              checked={draft.showBranchAddress}
              disabled={readOnly}
              onCheckedChange={(checked) => setDraft({ ...draft, showBranchAddress: checked })}
              label={tr('admin', 'print_the_branch_address')}
            />
            <Checkbox
              checked={draft.showBranchPhone}
              disabled={readOnly}
              onCheckedChange={(checked) => setDraft({ ...draft, showBranchPhone: checked })}
              label={tr('admin', 'print_the_branch_phone')}
            />
            <Checkbox
              checked={draft.showBranchLicence}
              disabled={readOnly}
              onCheckedChange={(checked) => setDraft({ ...draft, showBranchLicence: checked })}
              label={tr('admin', 'print_the_branch_licence_line_set_on_each_branch')}
            />
          </div>
          <Field label={tr('admin', 'footer')} hint={tr('admin', 'up_to_300_characters_printed_at_the_bottom_of')}>
            <Textarea rows={2} maxLength={300} value={draft.footer ?? ''} disabled={readOnly} onChange={(event) => setDraft({ ...draft, footer: event.target.value })} />
          </Field>
        </div>
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">{tr('admin', 'preview')}</p>
          <div className="rounded-md border bg-white p-4 text-black">
            {html ? <div className="letterhead-preview" dangerouslySetInnerHTML={{ __html: html }} /> : <SkeletonText lines={2} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
